import { cur } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Truck, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const TREND_COLORS = ['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1', '#84cc16', '#f97316'];

export function PlatformOverview({ summary, seedDefaults, trend }) {
  const series = trend?.series || [];
  // Only chart platforms that actually have sales in the period (keeps it readable)
  const activeNames = (trend?.platforms || []).filter(nm => series.some(pt => (pt[nm] || 0) > 0));
  return (
    <div className="space-y-4">
      {/* iter961 — Per-platform sales trend over the selected period */}
      {series.length > 0 && activeNames.length > 0 && (
        <Card data-testid="platform-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-outfit">
              <TrendingUp size={16} className="text-primary" /> Sales Trend by Platform
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {activeNames.map((nm, i) => (
                  <Line key={nm} type="monotone" dataKey={nm} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {summary.platforms.map(p => (
        <Card key={p.platform_id} className="hover:shadow-md transition-shadow" data-testid={`platform-card-${p.platform_id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span className="font-outfit">{p.platform_name}</span>
              <Badge variant={p.pending_amount > 0 ? 'destructive' : 'secondary'}>
                {p.pending_amount > 0 ? `${cur()} ${p.pending_amount.toLocaleString()} pending` : 'Settled'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Sales:</span>
                <span className="font-medium">{cur()} {p.total_sales.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Received:</span>
                <span className="font-medium text-success">{cur()} {p.total_received.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Commission ({p.commission_rate}%):</span>
                <span className="font-medium text-warning">{cur()} {p.total_commission.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t">
                <span>{p.sales_count} sales</span>
                <span>{p.payments_count} payments</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {summary.platforms.length === 0 && (
        <Card className="col-span-full">
          <CardContent className="py-8 text-center text-muted-foreground">
            <Truck size={48} className="mx-auto mb-3 opacity-30" />
            <p>No platform sales yet. Add a platform and record online sales.</p>
            <Button variant="outline" onClick={seedDefaults} className="mt-4">Load Default Platforms</Button>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
